/**
 * PATH: packages/ui/src/calendar/eventFilters.ts
 * FILE_ID: UI-CALENDAR-EVENT-FILTERS-001
 * PURPOSE: Pure provider-filter logic for the unified calendar chips
 *          (All / Internal / Outlook / Google Meet). Maps each
 *          occurrence onto the chip categories it belongs to and filters
 *          a stream by the active set.
 * WHY: Phase 4 of the calendar popup-unification work simplified the
 *      cluttered five-chip row (Internal/Teams/Google Meet/Outlook/Google
 *      Calendar) down to three provider ECOSYSTEMS: Internal,
 *      Outlook (Microsoft + Teams) and Google Meet (all Google, with or
 *      without a Meet link). Operators think in ecosystems, not in the
 *      source-vs-meeting-kind cross product.
 * FLOW:
 * ┌────────────┐   ┌─────────────────────┐   ┌────────────────────┐
 * │ occurrence │ → │ memberships(source, │ → │ matches active set?│
 * │            │   │  meeting url)       │   │ (union semantics)  │
 * └────────────┘   └─────────────────────┘   └────────────────────┘
 * DEPENDENCIES:
 * - ./providers: eventProviderFromSource, meetingCreateProviderFromUrl
 * - ./types: EventOccurrence
 * RELATED:
 * - ./components/CalendarFilterChips.tsx: renders the chip row
 * - ./state.ts: persists the active filter set
 */

import {
  eventProviderFromSource,
  meetingCreateProviderFromUrl,
} from './providers'
import type { EventOccurrence } from './types'

/**
 * One chip category — a provider ECOSYSTEM, not a source/meeting-kind
 * cross product. `outlook` covers Microsoft-sourced events AND Teams
 * meetings; `google_meet` covers all Google-sourced events (with or
 * without a Meet link) AND Google Meet links on internal events. An
 * event can belong to MORE THAN ONE (an internal event carrying a Teams
 * link is in both `internal` and `outlook`), which is why filtering uses
 * union semantics. `all` is not a member — it is the UI's "clear the
 * set" affordance, represented by an empty active set.
 */
export type CalendarProviderFilter =
  | 'internal'
  | 'outlook'
  | 'google_meet'

/** Chip metadata in display order. Mirrors the Phase-4 spec list. */
export const PROVIDER_FILTERS: ReadonlyArray<{
  id: CalendarProviderFilter
  label: string
}> = [
  { id: 'internal', label: 'Internal' },
  { id: 'outlook', label: 'Outlook' },
  { id: 'google_meet', label: 'Google Meet' },
]

const VALID_FILTERS = new Set<string>(PROVIDER_FILTERS.map((f) => f.id))

/**
 * The chip categories an occurrence belongs to.
 * Pure: f(occurrence) → categories[].
 *
 * Source axis → exactly one of internal/outlook/google_meet (Microsoft
 * source ⇒ outlook, Google source ⇒ google_meet, else internal).
 * Meeting axis → folds onto the matching ecosystem chip so an INTERNAL
 * event carrying a provider link is still reachable: a Teams link adds
 * `outlook`, a Google Meet link adds `google_meet`. (Zoom/other URLs add
 * no chip — joinable but not an ecosystem we slice by.)
 */
export function eventFilterMemberships(
  o: EventOccurrence,
): CalendarProviderFilter[] {
  const out: CalendarProviderFilter[] = []

  switch (eventProviderFromSource(o.source)) {
    case 'microsoft':
      out.push('outlook')
      break
    case 'google':
      out.push('google_meet')
      break
    default:
      out.push('internal')
  }

  const meeting = meetingCreateProviderFromUrl(o.online_meeting_url)
  if (meeting === 'teams' && !out.includes('outlook')) out.push('outlook')
  else if (meeting === 'google_meet' && !out.includes('google_meet'))
    out.push('google_meet')

  return out
}

/**
 * Does an occurrence survive the active filter set?
 * Pure: f(occurrence, active) → boolean.
 *
 * Empty active set ⇒ "All" ⇒ everything passes. Otherwise the event
 * passes if ANY of its memberships is active (union / OR semantics, so
 * combining chips widens the result the way operators expect).
 */
export function matchesProviderFilters(
  o: EventOccurrence,
  active: ReadonlyArray<CalendarProviderFilter>,
): boolean {
  if (active.length === 0) return true
  const memberships = eventFilterMemberships(o)
  return memberships.some((m) => active.includes(m))
}

/**
 * Filter a stream by the active set. Returns a NEW array (never mutates
 * the input). Empty active set returns a shallow copy of everything.
 * Pure: f(occurrences, active) → occurrences[].
 */
export function applyProviderFilters(
  occurrences: ReadonlyArray<EventOccurrence>,
  active: ReadonlyArray<CalendarProviderFilter>,
): EventOccurrence[] {
  if (active.length === 0) return [...occurrences]
  return occurrences.filter((o) => matchesProviderFilters(o, active))
}

/**
 * Toggle a single chip in/out of the active set, preserving display
 * order. Pure: f(active, chip) → next active set.
 */
export function toggleProviderFilter(
  active: ReadonlyArray<CalendarProviderFilter>,
  chip: CalendarProviderFilter,
): CalendarProviderFilter[] {
  const has = active.includes(chip)
  const next = has ? active.filter((c) => c !== chip) : [...active, chip]
  // Re-order to the canonical PROVIDER_FILTERS order for a stable URL /
  // localStorage shape regardless of click order.
  return PROVIDER_FILTERS.map((f) => f.id).filter((id) => next.includes(id))
}

/**
 * Sanitise an untrusted active set (from localStorage / props): drop
 * unknown values and de-dupe, keeping canonical order. Pure.
 */
export function normalizeProviderFilters(
  raw: unknown,
): CalendarProviderFilter[] {
  if (!Array.isArray(raw)) return []
  const seen = new Set<string>()
  for (const v of raw) if (typeof v === 'string' && VALID_FILTERS.has(v)) seen.add(v)
  return PROVIDER_FILTERS.map((f) => f.id).filter((id) => seen.has(id))
}
